import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Mail, Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Reusable newsletter signup block.
 * Props:
 *  - source: tracking source ("blog", "blog-post", "landing", etc.)
 *  - variant: "card" (default, full block) | "inline" (compact for footers)
 */
export default function NewsletterForm({ source = "blog", variant = "card" }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ok | error
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setStatus("error");
      setMessage("Email invalide");
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      const { data } = await api.post("/newsletter/subscribe", { email: email.trim(), source });
      setStatus("ok");
      setMessage(
        data.status === "reactivated"
          ? "Bon retour ! Tu es à nouveau inscrit."
          : data.status === "already_subscribed"
          ? "Tu es déjà inscrit. Merci !"
          : "C'est bon ! Vérifie ta boîte mail pour le mail de bienvenue."
      );
      setEmail("");
    } catch (err) {
      setStatus("error");
      setMessage(formatApiErrorDetail(err.response?.data?.detail) || "Impossible d'enregistrer ton inscription. Réessaie.");
    }
  };

  if (variant === "inline") {
    return (
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 max-w-md" data-testid={`newsletter-form-${source}`}>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="ton.email@etudiant.fr"
          aria-label="Adresse email"
          className="flex-1 px-4 py-2.5 rounded-full border border-slate-300 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none text-sm"
          data-testid="newsletter-email-input"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className="px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-1.5"
          data-testid="newsletter-submit"
        >
          {status === "loading" ? "..." : (status === "ok" ? <><Check size={14} /> Inscrit</> : <>S'inscrire <ArrowRight size={14} /></>)}
        </button>
        {message && (
          <p className={`text-xs mt-1 sm:mt-0 sm:basis-full ${status === "error" ? "text-red-600" : "text-emerald-600"}`} data-testid="newsletter-message">
            {message}
          </p>
        )}
      </form>
    );
  }

  // Card variant
  return (
    <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-violet-50 via-white to-blue-50 p-7 sm:p-9" data-testid={`newsletter-card-${source}`}>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 text-white flex items-center justify-center shadow-lg shadow-violet-500/30 shrink-0">
          <Mail size={22} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display text-xl sm:text-2xl font-bold text-slate-900 leading-tight">
            Reçois 1 conseil étudiant par semaine
          </p>
          <p className="text-sm text-slate-600 mt-1.5 max-w-md">
            Démarches, bons plans, alternance, logement, IA. Du concret, jamais de spam. Désinscription en 1 clic.
          </p>
          <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2" data-testid={`newsletter-form-${source}`}>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ton.email@etudiant.fr"
              aria-label="Adresse email"
              className="flex-1 px-4 py-3 rounded-full border border-slate-300 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none text-sm bg-white"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="px-6 py-3 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 text-white text-sm font-semibold shadow-md shadow-violet-500/20 hover:from-blue-700 hover:to-violet-700 disabled:opacity-60 transition-all inline-flex items-center justify-center gap-1.5"
              data-testid="newsletter-submit"
            >
              {status === "loading" ? "..." : (status === "ok" ? <><Check size={14} /> Inscrit !</> : <>Je m'inscris <ArrowRight size={14} /></>)}
            </button>
          </form>
          {message && (
            <p className={`text-xs mt-3 ${status === "error" ? "text-red-600" : "text-emerald-700"}`} data-testid="newsletter-message">
              {message}
            </p>
          )}
          <p className="text-[11px] text-slate-500 mt-3">
            En t'inscrivant, tu acceptes notre <Link to="/legal/confidentialite" className="underline hover:text-violet-700">politique de confidentialité</Link>. Données stockées en France · Conforme RGPD.
          </p>
        </div>
      </div>
    </div>
  );
}
